import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  Loader2,
  Check,
  CheckCheck,
  FileText,
  PlayCircle,
  Radio,
} from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { toast } from "sonner";

const ICON_BY_TYPE = {
  artigo: FileText,
  video: PlayCircle,
  ep_radio: Radio,
};

/**
 * Badge de notificações in-app — Fase 3 do refactor.
 *
 * Renderizada no header global (somente para usuários autenticados).
 * Mostra contador agregado de não-lidas; abre popover com a lista
 * dos 10 mais recentes. Cada item linka para o conteúdo publicado;
 * marcar como lido é automático ao clicar.
 */
export default function NotificationsBadge() {
  const { user, authClient } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState(null);
  const wrapRef = useRef(null);

  // Poll a cada 60s o count de não-lidas; reduz para 30s quando o popover está aberto.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchCount = () => {
      authClient
        .get("/notifications/unread-count")
        .then((r) => { if (!cancelled) setUnread(r.data.unread_count); })
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, open ? 30000 : 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, authClient, open]);

  useEffect(() => {
    if (!open || !user) return;
    setItems(null);
    authClient
      .get("/notifications", { params: { limit: 10 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [open, user, authClient]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!user) return null;

  const markRead = async (n) => {
    if (n.read) return;
    try {
      await authClient.post(`/notifications/${n.id}/read`);
      setItems((list) => (list || []).map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((c) => Math.max(0, c - 1));
    } catch {
      /* silencioso */
    }
  };

  const markAllRead = async () => {
    try {
      const r = await authClient.post("/notifications/read-all");
      toast.success(`${r.data.marked} marcadas como lidas`);
      setItems((list) => (list || []).map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div ref={wrapRef} className="relative inline-block" data-testid="notifications-wrap">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 rounded-full border border-slate-700/60 bg-slate-950/60 hover:border-amber-400/60 text-slate-300 hover:text-amber-300 transition inline-flex items-center justify-center"
        aria-label="Notificações"
        data-testid="notifications-trigger"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notifications-badge-count"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute z-50 right-0 mt-2 w-80 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden"
          data-testid="notifications-popover"
        >
          <header className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <h3 className="font-serif text-sm font-bold text-white">Notificações</h3>
            {(items || []).some((x) => !x.read) && (
              <button
                onClick={markAllRead}
                className="text-[11px] text-amber-300 hover:text-amber-200 inline-flex items-center gap-1"
                data-testid="notifications-mark-all-read"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Marcar todas
              </button>
            )}
          </header>
          <div className="max-h-[60vh] overflow-y-auto">
            {items === null ? (
              <div className="px-4 py-6 text-center text-slate-400 text-xs">
                <Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> Carregando…
              </div>
            ) : items.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-slate-500">
                Você ainda não tem notificações.
                <br />
                <span className="text-slate-600 mt-1 inline-block">
                  Comece seguindo professores(as) com o botão <span className="text-emerald-300">+ Seguir</span>.
                </span>
              </div>
            ) : (
              <ul className="divide-y divide-slate-800">
                {items.map((n) => {
                  const Icon = ICON_BY_TYPE[n.source_type] || FileText;
                  return (
                    <li key={n.id}>
                      <Link
                        to={n.source_url}
                        onClick={() => { markRead(n); setOpen(false); }}
                        className={`flex items-start gap-3 px-4 py-3 hover:bg-slate-800/60 transition ${
                          n.read ? "opacity-60" : ""
                        }`}
                        data-testid={`notifications-item-${n.id}`}
                      >
                        {!n.read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" aria-hidden />
                        )}
                        <Icon className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] text-sky-300 truncate">{n.author_name}</div>
                          <div className="text-xs text-white font-medium leading-snug line-clamp-2">
                            {n.source_title}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {new Date(n.created_at).toLocaleDateString("pt-BR")}
                          </div>
                        </div>
                        {n.read && <Check className="w-3 h-3 text-slate-600 shrink-0 mt-1" />}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <footer className="border-t border-slate-800 px-4 py-2 text-center">
            <Link
              to="/notificacoes"
              onClick={() => setOpen(false)}
              className="text-[11px] text-slate-400 hover:text-amber-300"
              data-testid="notifications-see-all"
            >
              Ver todas as notificações
            </Link>
          </footer>
        </div>
      )}
    </div>
  );
}
